import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { FiStar, FiX } from 'react-icons/fi';
import { Slot } from '@radix-ui/react-slot';
import IconVerified from '@/app/components/Icons/IconVerified';

const cardVariants = cva('relative rounded-xl bg-white py-4 shadow-dropshadow-sm transition-all duration-150 max-w-full flex', {
  variants: {
    orientation: {
      vertical: 'flex-col gap-y-4',
      horizontal: 'flex-row gap-4'
    },
    hover: {
      true: 'hover:shadow-dropshadow-md'
    },
    border: {
      true: 'border border-solid border-neutral-lightest'
    },
    isClickable: {
      true: 'cursor-pointer'
    },
    isDanger: {
      true: 'border border-solid border-danger'
    },
    isWarning: {
      true: 'border border-solid border-warning'
    }
  },
  defaultVariants: {
    orientation: 'vertical',
    hover: false,
    border: true,
    isClickable: false,
    isDanger: false,
    isWarning: false
  }
});

const Card = React.forwardRef(({ hover, border, isClickable, isDanger, isWarning, orientation, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ hover, border, isClickable, isDanger, isWarning, orientation }), className)}
    {...props}
  />
));
Card.displayName = 'Card';

const cardWrapperVariants = cva('grid gap-5 relative z-0', {
  variants: {
    orientation: {
      horizontal: 'grid-cols-[repeat(auto-fill,minmax(250px,1fr))]',
      vertical: 'grid-cols-1'
    },
    size: {
      md: 'grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'
    }
  },
  defaultVariants: {
    orientation: 'horizontal'
  }
});

const CardWrapper = React.forwardRef(({ className, size, orientation, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-wrapper', cardWrapperVariants({ size, orientation }), className)}
    {...props}
  />
));
CardWrapper.displayName = 'CardWrapper';

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-header', 'flex flex-col gap-2 px-4', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-card-title', 'mb-0 line-clamp-3 min-w-5 flex-grow text-base font-bold leading-tight text-primary-dark', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-card-description', 'mb-4 block truncate text-xs font-light text-neutral', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardText = React.forwardRef(({ className, variant = 'default', ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-card-text',
      // Estilo base
      'mb-0 text-xs font-light',
      // Variant
      variant === 'default' && 'text-neutral',
      variant === 'danger' && 'text-danger',
      className
    )}
    {...props}
  />
));
CardText.displayName = 'CardText';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-content', 'flex flex-grow items-start gap-4 px-4', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-footer', 'flex items-center px-4', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

const CardBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-body', 'flex w-0 flex-grow flex-col gap-4 self-stretch', className)}
    {...props}
  />
));
CardBody.displayName = 'CardBody';

const CardBodyHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-body-header', 'min-h-7', className)}
    {...props}
  />
));
CardBodyHeader.displayName = 'CardBodyHeader';

const CardBodyContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-body-content', 'flex flex-grow flex-col justify-end gap-4', className)}
    {...props}
  />
));
CardBodyContent.displayName = 'CardBodyContent';

const CardBodyHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-body-header-content', 'mb-2 flex w-full items-center gap-4', className)}
    {...props}
  />
));
CardBodyHeaderContent.displayName = 'CardBodyHeaderContent';

const cardKPIVariants = cva(
  'text-neutral text-sm font-bold min-h-[44px] p-1 rounded border border-solid border-bright flex items-center justify-center flex-wrap gap-x-2 gap-y-0.5 mb-0 text-center [&>svg]:text-primary [&>svg]:text-primary [&>svg]:shrink-0 [&>svg]:min-w-[16px] [&>svg]:relative transition-all duration-150',
  {
    variants: {
      clickable: {
        true: 'cursor-pointer hover:bg-slate-50',
        false: 'cursor-default'
      }
    },
    defaultVariants: {
      clickable: false
    }
  }
);

const CardKpi = React.forwardRef(({ className, clickable, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-kpi', cardKPIVariants({ clickable }), className)}
    {...props}
  />
));
CardKpi.displayName = 'CardKpi';

const CardKpiGrid = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-kpi-grid', 'grid grid-cols-2 gap-3', className)}
    {...props}
  />
));
CardKpiGrid.displayName = 'CardKpiGrid';

const CardKpiTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn('c-card-kpi-title', 'mb-0 w-full text-xs font-light text-neutral', className)}
    {...props}
  />
));
CardKpiTitle.displayName = 'CardKpiTitle';

const CardIcon = React.forwardRef(({ className, roundedFull = false, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      className={cn(
        'c-card-icon',
        // Estilos base
        'relative inline-block shrink-0 rounded border-0 bg-primary-lightest p-1 leading-none text-primary',
        // Variantes
        roundedFull && 'rounded-full',
        className
      )}
      {...props}
    />
  );
});
CardIcon.displayName = 'CardIcon';

const CardIconVerified = React.forwardRef(({ className, ...props }, ref) => (
  <IconVerified
    ref={ref}
    className={cn('z-1 absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2', className)}
    {...props}
  />
));
CardIconVerified.displayName = 'CardIconVerified';

const CardIconImg = React.forwardRef(({ className, isSemicircle, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-icon-img',
      // styles
      'relative flex min-w-5 text-primary [&>svg]:w-full',
      className
    )}
    {...props}
  >
    {children}
    {isSemicircle && <span className="absolute inset-0 left-1/2 inline-block bg-white/80" />}
  </div>
));
CardIconImg.displayName = 'CardIconImg';

const cardProgressVariants = cva('flex items-center gap-2 text-xs font-light text-primary max-w-full', {
  variants: {
    variant: {
      default: '',
      cover: [
        'w-72',
        '[&_.c-card-progress-container]:h-3 [&_.c-card-progress-container]:bg-white/20',
        '[&_.c-card-progress-bar]:rounded-[10px] [&_.c-card-progress-bar]:bg-gradient-to-b [&_.c-card-progress-bar]:from-[#6FCF97] [&_.c-card-progress-bar]:to-[#19C761]',
        '[&_.c-card-progress-text]:text-white'
      ]
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const CardProgress = React.forwardRef(({ variant, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-progress', cardProgressVariants({ variant }), className)}
    {...props}
  />
));
CardProgress.displayName = 'CardProgress';

const CardProgressContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-progress-container', 'h-[6px] w-full flex-grow overflow-hidden rounded-xl bg-neutral-lightest', className)}
    {...props}
  />
));
CardProgressContainer.displayName = 'CardProgressContainer';

const CardProgressBar = React.forwardRef(({ percentage = 0, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-card-progress-bar', 'h-full bg-primary transition-all delay-500 duration-1000 ease-in-out', className)}
    role="progressbar"
    style={{ width: `${parseFloat(percentage)}%` }}
    aria-valuenow={parseFloat(percentage)}
    aria-valuemin="0"
    aria-valuemax="100"
    {...props}
  />
));
CardProgressBar.displayName = 'CardProgressBar';

const CardProgressText = React.forwardRef(({ percentage = 0, toFixed = 0, className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-card-progress-text', 'mb-0 shrink-0', className)}
    {...props}
  >
    {`${parseFloat(percentage)?.toFixed(toFixed)}%`}
  </p>
));
CardProgressText.displayName = 'CardProgressText';

const CardProgressWrapper = React.forwardRef(({ variant, percentage = 0, toFixed = 0, showText = true, className, ...props }, ref) => (
  <CardProgress
    ref={ref}
    variant={variant}
    className={cn('c-card-progress-wrapper', className)}
    {...props}
  >
    <CardProgressContainer>
      <CardProgressBar percentage={percentage} />
    </CardProgressContainer>
    {showText && (
      <CardProgressText
        percentage={percentage}
        toFixed={toFixed}
      />
    )}
  </CardProgress>
));
CardProgressWrapper.displayName = 'CardProgressWrapper';

const CardDuel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-duel',
      // Estilos base
      'flex grow flex-wrap items-center justify-between',
      'rounded-xl bg-neutral-bright p-3',
      className
    )}
    {...props}
  />
));
CardDuel.displayName = 'CardDuel';

const CardDuelProfile = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-duel-profile',
      // Estilos base
      'flex w-10 grow flex-col items-center self-start',
      'text-xs text-neutral',
      className
    )}
    {...props}
  />
));
CardDuelProfile.displayName = 'CardDuelProfile';

const CardDuelAvatar = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-duel-avatar',
      // Estilos base
      'relative',
      className
    )}
    {...props}
  />
));
CardDuelAvatar.displayName = 'CardDuelAvatar';

const CardDuelName = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-card-duel-name',
      // Estilos base
      'line-clamp-2 cursor-default text-center',
      className
    )}
    {...props}
  />
));
CardDuelName.displayName = 'CardDuelName';

const CardDuelScore = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-duel-score',
      // Estilos base
      'flex shrink-0 items-center gap-2',
      'text-lg font-bold',
      className
    )}
    {...props}
  />
));
CardDuelScore.displayName = 'CardDuelScore';

const CardDuelVs = React.forwardRef(({ className, ...props }, ref) => (
  <FiX
    ref={ref}
    className={cn(
      'c-card-duel-vs',
      // Estilos base
      'size-5 text-danger',
      className
    )}
    {...props}
  />
));
CardDuelVs.displayName = 'CardDuelVs';

const CardDuelNumber = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-card-duel-number',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
CardDuelNumber.displayName = 'CardDuelNumber';

const CardDuelStar = React.forwardRef(({ className, ...props }, ref) => (
  <FiStar
    ref={ref}
    className={cn(
      'c-card-duel-star',
      // Estilos base
      'absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center rounded-full border border-[#74dbe1] bg-[#74dbe1] fill-current p-0.5 text-white',
      className
    )}
    {...props}
  />
));
CardDuelStar.displayName = 'CardDuelStar';

const CardChallenged = React.forwardRef(({ className, isSelected, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-challenged',
      // Estilos base
      'mb-3 flex items-center justify-between rounded-[10px]',
      // Estilos da variante 'selected'
      isSelected && 'bg-neutral-bright p-3',
      className
    )}
    {...props}
  />
));
CardChallenged.displayName = 'CardChallenged';

const CardChallengedProfile = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-challenged-avatar',
      // Estilos de layout
      'flex items-center gap-3',
      className
    )}
    {...props}
  />
));
CardChallengedProfile.displayName = 'CardChallengedProfile';

const CardChallengedName = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-card-challenged-name',
      // Estilos de layout
      '',
      className
    )}
    {...props}
  />
));
CardChallengedName.displayName = 'CardChallengedName';

const CardChallengedWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-challenged-wrapper',
      // Estilos de layout
      'h-[500px] overflow-auto p-2',
      className
    )}
    {...props}
  />
));
CardChallengedWrapper.displayName = 'CardChallengedWrapper';

const CardAvatar = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-card-avatar',
      // styles
      'flex flex-col gap-1',
      className
    )}
    {...props}
  />
));
CardAvatar.displayName = 'CardAvatar';

const CardAvatarTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn(
      'c-card-avatar-title',
      // styles
      'mb-0 text-xs font-light text-neutral',
      className
    )}
    {...props}
  />
));
CardAvatarTitle.displayName = 'CardAvatarTitle';

export {
  Card,
  CardWrapper,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardText,
  CardContent,
  CardBody,
  CardBodyHeader,
  CardBodyContent,
  CardBodyHeaderContent,
  CardKpi,
  CardKpiGrid,
  CardKpiTitle,
  CardIcon,
  CardIconVerified,
  CardIconImg,
  CardProgress,
  CardProgressContainer,
  CardProgressBar,
  CardProgressText,
  CardProgressWrapper,
  CardDuel,
  CardDuelProfile,
  CardDuelAvatar,
  CardDuelName,
  CardDuelScore,
  CardDuelVs,
  CardDuelNumber,
  CardDuelStar,
  CardChallenged,
  CardChallengedProfile,
  CardChallengedName,
  CardChallengedWrapper,
  CardAvatar,
  CardAvatarTitle
};
